import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'react-toastify';

const BulkUploadModal = ({ isOpen, onClose, onUploadSuccess }) => {
  const [file, setFile] = useState(null);
  const [customTag, setCustomTag] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);

  // Drag and drop handler
  const onDrop = useCallback((acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile) {
      // Validate file type
      if (!uploadedFile.name.endsWith('.xlsx')) {
        toast.error('Please upload .xlsx file only');
        return;
      }
      setFile(uploadedFile);
      toast.success(`File "${uploadedFile.name}" selected`);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    multiple: false,
    maxSize: 5242880 // 5MB
  });

  // Download template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get('/api/customers/template/download', {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer-bulk-upload-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      toast.success('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template');
    }
  };

  // Handle upload
  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    if (customTag) {
      formData.append('customTag', customTag);
    }

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);

      const response = await axios.post('/api/customers/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Show results
      setUploadResults(response.data);

      toast.success(`✅ Uploaded ${response.data.uploaded} customers successfully!`);

      // Call success callback
      if (onUploadSuccess) {
        onUploadSuccess(response.data);
      }

    } catch (error) {
      console.error('Error uploading file:', error);
      
      if (error.response?.data?.errors) {
        // Show validation errors
        const errorMsg = error.response.data.errors
          .slice(0, 3)
          .map(err => `Row ${err.row}: ${err.message}`)
          .join('\n');
        toast.error(`Upload failed:\n${errorMsg}${error.response.data.errors.length > 3 ? '\n...and more' : ''}`);
        setUploadResults(error.response.data);
      } else {
        toast.error(error.response?.data?.message || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  // Reset modal
  const handleClose = () => {
    setFile(null);
    setCustomTag('');
    setUploading(false);
    setUploadProgress(0);
    setUploadResults(null);
    onClose();
  };

  // Close and refresh
  const handleCloseAndRefresh = () => {
    handleClose();
    window.location.reload(); // Or call a parent refresh function
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">📤 Bulk Upload Customers</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Show results if upload completed */}
          {uploadResults ? (
            <div className="space-y-4">
              {uploadResults.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <span className="text-4xl mr-3">✅</span>
                    <h3 className="text-xl font-bold text-green-800">Upload Complete!</h3>
                  </div>

                  <div className="mb-4">
                    <p className="text-lg text-gray-700">
                      <strong>Successfully uploaded:</strong> {uploadResults.uploaded} customers
                    </p>
                    {uploadResults.skipped > 0 && (
                      <p className="text-lg text-gray-700">
                        <strong>Skipped (duplicates):</strong> {uploadResults.skipped} customers
                      </p>
                    )}
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Tag applied:</strong> {uploadResults.tag}
                    </p>
                  </div>

                  {uploadResults.skippedDetails && uploadResults.skippedDetails.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
                      <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Skipped Details:</h4>
                      <ul className="space-y-1 text-sm text-gray-700 max-h-40 overflow-y-auto">
                        {uploadResults.skippedDetails.map((skip, index) => (
                          <li key={index}>
                            Row {skip.row}: <strong>{skip.name}</strong> - {skip.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleCloseAndRefresh}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                    >
                      View Uploaded Customers
                    </button>
                    <button
                      onClick={handleClose}
                      className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <span className="text-4xl mr-3">❌</span>
                    <h3 className="text-xl font-bold text-red-800">Upload Failed</h3>
                  </div>

                  <p className="text-lg text-gray-700 mb-4">
                    Found {uploadResults.errorCount || uploadResults.errors?.length || 0} validation errors
                  </p>

                  {uploadResults.errors && uploadResults.errors.length > 0 && (
                    <div className="bg-white border border-red-300 rounded p-4 mb-4 max-h-60 overflow-y-auto">
                      <h4 className="font-semibold text-red-800 mb-2">Errors:</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        {uploadResults.errors.slice(0, 10).map((error, index) => (
                          <li key={index} className="border-b border-gray-200 pb-2">
                            <strong>Row {error.row}:</strong> {error.field} - {error.message}
                            {error.data && (
                              <div className="text-xs text-gray-500 mt-1">
                                Customer: {error.data.name || 'N/A'}
                              </div>
                            )}
                          </li>
                        ))}
                        {uploadResults.errors.length > 10 && (
                          <li className="text-gray-500 italic">
                            ...and {uploadResults.errors.length - 10} more errors
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={() => setUploadResults(null)}
                    className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Step 1: Download Template */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-800 mb-2">Step 1: Download Template</h3>
                <p className="text-sm text-gray-700 mb-3">
                  Download our Excel template with proper formatting and instructions.
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-2"
                >
                  📥 Download Excel Template
                </button>
              </div>

              {/* Step 2: Upload File */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Step 2: Upload Your File</h3>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${
                    isDragActive
                      ? 'border-blue-500 bg-blue-50'
                      : file
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
                  }`}
                >
                  <input {...getInputProps()} />
                  {file ? (
                    <div>
                      <p className="text-4xl mb-2">✅</p>
                      <p className="text-lg font-semibold text-green-700">{file.name}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                      <p className="text-sm text-blue-600 mt-2">Click or drag to change file</p>
                    </div>
                  ) : isDragActive ? (
                    <div>
                      <p className="text-4xl mb-2">📤</p>
                      <p className="text-lg font-semibold text-blue-700">Drop the file here</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-4xl mb-2">📄</p>
                      <p className="text-lg font-semibold text-gray-700">
                        Drag & drop Excel file here
                      </p>
                      <p className="text-sm text-gray-500 mt-1">or click to browse</p>
                      <p className="text-xs text-gray-400 mt-2">
                        (.xlsx files only, max 1000 rows, 5MB limit)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Step 3: Configure */}
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">Step 3: Configure (Optional)</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Custom Tag (optional)
                    </label>
                    <input
                      type="text"
                      value={customTag}
                      onChange={(e) => setCustomTag(e.target.value)}
                      placeholder="e.g., Glofox, Salon-clients"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={uploading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 If empty, auto-tag will be: "Bulk-Nov08" (today's date)
                    </p>
                  </div>
                </div>
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {uploadProgress < 30
                        ? 'Uploading file...'
                        : uploadProgress < 60
                        ? 'Validating data...'
                        : uploadProgress < 90
                        ? 'Checking for duplicates...'
                        : 'Finalizing...'}
                    </span>
                    <span className="text-sm font-medium text-blue-600">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={handleClose}
                  disabled={uploading}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <span>🚀</span> Upload & Import
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;